import React from "react"
import {
    Alert,
    Autocomplete,
    Box,
    Button, Collapse,
    Divider,
    Grid2 as Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {dataAttributes, primaryKeys} from "@/utils";
import {Add, Delete} from "@mui/icons-material";

const {REACT_APP_ENV} = process.env

export default function ProductsGS1Form(props: any) {

    // disabled: the whole GS1 form is gated until the product's parents
    // (brand + range) are selected.
    const {usedModule, GS1Product, setGS1Product, handleGS1LookUp, verifiedGtin, lookUpStale, disabled, readOnlyData, t} = props

    // Data inputs are also locked on a recovered creation (readOnlyData):
    // the already-created QR Code encodes this data's short URL, so only
    // the Look Up (re-validation) stays available.
    const dataLocked = disabled || readOnlyData

    // Debug
    const generateRandomGTIN = () => {
        let gtin = Math.floor(Math.random() * (9999999999999 - 1000000000000) + 1000000000000)
        setGS1Product({...GS1Product, primaryKeyValue: "0" + String(gtin)})
    }

    // KQ values
    const returnKeyQualifierValue = (key: any) => {
        let keyQualifiers = GS1Product.key_identifiers
        let keyQualifier = keyQualifiers.filter((kq: any) => kq.key === key)

        if (!!keyQualifier[0]) return keyQualifier[0].value
        else return ""
    }

    const gs1KeysError = () => {

        return (
            (!!GS1Product.primaryKeyValue && GS1Product.primaryKeyValue.includes(" ")) ||
            (returnKeyQualifierValue("22") && returnKeyQualifierValue("22").includes(" ")) ||
            (returnKeyQualifierValue("10") && returnKeyQualifierValue("10").includes(" ")) ||
            (returnKeyQualifierValue("21") && returnKeyQualifierValue("21").includes(" "))
        )
    }

    // primaryKeys
    const renderPrimaryKeySelector = () => {
        if (!primaryKeys) return <></>
        return (
            <>
                <Grid size={12}>
                    <Typography variant={"body1"} fontWeight={600}>
                        {t("products:primary_key")}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Autocomplete
                        disabled={dataLocked}
                        value={GS1Product?.primaryKey}
                        onChange={(_e, value: any) => setGS1Product({...GS1Product, primaryKey: !!value ? value.key : "" })}
                        renderInput={(params) => (<TextField {...params} label={t("products:gs1_primary_key")} />)}
                        options={primaryKeys}
                        noOptionsText={t("products:no_options")}
                        getOptionLabel={(option) => {
                            if (!option) return ""
                            if (typeof option === "string") {
                                let selectedPrimaryKey = primaryKeys.filter((key: any) => key.key === option)
                                if (!!selectedPrimaryKey) return selectedPrimaryKey[0].label
                                else return ""
                            }
                            return option.label
                        }}
                        renderOption={(props, option) => (<Box component={"li"} {...props}>{option.label}</Box>)}
                    />
                </Grid>
                <Grid size={12}>
                    {
                        (!!REACT_APP_ENV && REACT_APP_ENV === "dev") &&
                        <Button onClick={() => generateRandomGTIN()}>
                            Generate random GTIN
                        </Button>
                    }
                    <TextField
                        size={"small"}
                        label={t("products:value")}
                        value={GS1Product?.primaryKeyValue}
                        disabled={dataLocked}
                        error={!!GS1Product.primaryKeyValue && GS1Product.primaryKeyValue.includes(" ")}
                        onChange={(e) => setGS1Product({...GS1Product, primaryKeyValue: e.target.value})}
                        fullWidth
                    />
                    <Collapse sx={{ mt: 2 }} in={GS1Product?.primaryKey === "01" && !!GS1Product.primaryKeyValue && ((GS1Product.primaryKeyValue.startsWith("0") && GS1Product.primaryKeyValue.length < 14) || (!GS1Product.primaryKeyValue.startsWith("0") && GS1Product.primaryKeyValue.length !== 13))}>
                        <Alert severity={"warning"}>
                            <Typography>{t("gtin_warning")}</Typography>
                        </Alert>
                    </Collapse>
                </Grid>
            </>
        )
    }

    // keyQualifiers
    const renderKeyQualifiersSelector = () => {

        const renderKeyQualifiersOptions = () => {

            const handleChangeKeyQualifiers = (key: any, value: any) => {

                let keyQualifiers = [...GS1Product.key_identifiers]
                let keyQualifier = keyQualifiers.filter((kq: any) => kq.key === key)

                if (!!keyQualifier[0]) {
                    let index = keyQualifiers.findIndex((kq: any) => kq.key === key)
                    keyQualifier[0] = { key: key, value: value }
                    keyQualifiers[index] = keyQualifier[0]
                } else {
                    keyQualifiers.push({ key: key, value: value })
                }

                setGS1Product({...GS1Product, key_identifiers: keyQualifiers})
            }

            const KQType0 = () => {
                return (
                    <Grid size={12}>
                        <Typography>{t("products:no_key_qualifiers")}</Typography>
                    </Grid>
                )
            }

            const KQTypeGTIN = () => {
                return (
                    <>
                        <Grid size={12}>
                            <TextField
                                size={"small"}
                                label={t("products:22")}
                                value={returnKeyQualifierValue("22")}
                                disabled={dataLocked}
                                error={returnKeyQualifierValue("22").includes(" ")}
                                onChange={(e) => handleChangeKeyQualifiers("22", e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                size={"small"}
                                label={t("products:10")}
                                value={returnKeyQualifierValue("10")}
                                disabled={dataLocked}
                                error={returnKeyQualifierValue("10").includes(" ")}
                                onChange={(e) => handleChangeKeyQualifiers("10", e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                size={"small"}
                                label={t("products:21")}
                                value={returnKeyQualifierValue("21")}
                                disabled={dataLocked}
                                error={returnKeyQualifierValue("21").includes(" ")}
                                onChange={(e) => handleChangeKeyQualifiers("21", e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    </>
                )
            }

            const KQType254 = () => {
                return (
                    <Grid size={12}>
                        <TextField
                            size={"small"}
                            label={t("products:254")}
                            value={returnKeyQualifierValue("254")}
                            disabled={dataLocked}
                            onChange={(e) => handleChangeKeyQualifiers("254", e.target.value)}
                            fullWidth
                        />
                    </Grid>
                )
            }

            const KQType8011 = () => {
                return (
                    <Grid size={12}>
                        <TextField
                            size={"small"}
                            label={t("products:8011")}
                            value={returnKeyQualifierValue("8011")}
                            disabled={dataLocked}
                            onChange={(e) => handleChangeKeyQualifiers("8011", e.target.value)}
                            fullWidth
                        />
                    </Grid>
                )
            }

            const KQType8019 = () => {
                return (
                    <Grid size={12}>
                        <TextField
                            size={"small"}
                            label={t("products:8019")}
                            value={returnKeyQualifierValue("8019")}
                            disabled={dataLocked}
                            onChange={(e) => handleChangeKeyQualifiers("8019", e.target.value)}
                            fullWidth
                        />
                    </Grid>
                )
            }

            switch (GS1Product.primaryKey) {
                case "01":
                case "8006": return KQTypeGTIN()
                case "414": return KQType254()
                case "8010": return KQType8011()
                case "8017":
                case "8018": return KQType8019()
                default: return KQType0()
            }
        }

        return (
            <>
                <Grid size={12}>
                    <Typography variant={"body1"} fontWeight={600}>
                        {t("products:key_qualifiers")} {t("products:optional")}
                    </Typography>
                </Grid>
                {renderKeyQualifiersOptions()}
            </>
        )
    }

    // dataAttributes
    const renderDataAttributesSelector = () => {

        const addDataAttribute = () => {
            let _dataAttributes = [...GS1Product.dataAttributes]
            _dataAttributes.push({ key: "", value: "" })
            setGS1Product({...GS1Product, dataAttributes: _dataAttributes})
        }

        const deleteDataAttribute = (index: any) => {
            let _dataAttributes = [...GS1Product.dataAttributes]
            _dataAttributes.splice(index, 1)
            setGS1Product({...GS1Product, dataAttributes: _dataAttributes})
        }

        const onChangeDataAttributeKey = (dataAttribute: any, index: number) => {
            let _dataAttributes = [...GS1Product.dataAttributes]
            _dataAttributes[index] = { key: !!dataAttribute ? dataAttribute.key : "", value: "" }
            setGS1Product({...GS1Product, dataAttributes: _dataAttributes})
        }

        const onChangeDataAttributeValue = (value: any, index: number) => {
            let _dataAttributes = [...GS1Product.dataAttributes]
            _dataAttributes[index] = { key: _dataAttributes[index].key, value: value }
            setGS1Product({...GS1Product, dataAttributes: _dataAttributes})
        }

        const renderDataAttributeForm = (_dataAttributeObject: any, index: number) => {
            return (
                <>
                    <Grid size={12}>
                        <Autocomplete
                            size={"small"}
                            ListboxProps={{
                                sx: {
                                    maxHeight: 300,
                                    "& li": {
                                        padding: "2px",
                                        fontSize: "15px"
                                    }
                                }
                            }}
                            disabled={dataLocked}
                            value={GS1Product.dataAttributes[index].key || ""}
                            onChange={(_e, value: any) => onChangeDataAttributeKey(value, index)}
                            renderInput={(params) => (<TextField {...params} label={t("products:gs1_data_attribute")} />)}
                            options={dataAttributes}
                            noOptionsText={t("products:no_options")}
                            getOptionLabel={(option) => {
                                if (!option) return ""
                                if (typeof option === "string") {
                                    let selectedDataAttribute = dataAttributes.filter((key: any) => key.key === option)
                                    if (!!selectedDataAttribute) return selectedDataAttribute[0].label
                                    else return ""
                                }
                                return option.label
                            }}
                            renderOption={(props, option) => (<Box component={"li"} {...props}>{option.label}</Box>)}
                        />
                    </Grid>
                    <Grid size={11}>
                        <TextField
                            size={"small"}
                            label={t("products:value")}
                            value={GS1Product.dataAttributes[index].value || ""}
                            disabled={dataLocked || !GS1Product.dataAttributes[index].key}
                            onChange={(e) => onChangeDataAttributeValue(e.target.value, index)}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ md: 1 }} sx={{ textAlign: "center" }}>
                        <Tooltip  title={t("products:delete")} placement={"top"} arrow>
                            <span>
                                <IconButton onClick={() => deleteDataAttribute(index)} disabled={dataLocked || GS1Product.dataAttributes.length === 1}>
                                    <Delete />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </>
            )
        }

        if (!dataAttributes) return <></>

        return (
            <>
                <Grid size={12}>
                    <Typography variant={"body1"} fontWeight={600}>
                        {t("products:data_attributes")} {t("products:optional")}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Button
                            size={"small"}
                            color={"secondary"}
                            variant={"contained"}
                            disableElevation
                            startIcon={<Add />}
                            onClick={() => addDataAttribute()}
                            disabled={dataLocked || GS1Product.dataAttributes.length === 5}
                        >
                            {t("products:add_data_attribute")}
                        </Button>
                        <Typography
                            sx={{ pt: "4px", color: GS1Product.dataAttributes.length === 5 ? "red" : "initial" }}
                            variant={"body2"}
                        >
                            <i>{t("products:data_attributes_number")} {GS1Product.dataAttributes.length || 0}/5</i>
                        </Typography>
                    </Stack>
                </Grid>
                {!!GS1Product.dataAttributes && GS1Product.dataAttributes.map((da: any, index: number) => {
                    return (
                        <Grid size={12} key={index}>
                            <Paper sx={{ p: 2, backgroundColor: "#f6f6f6" }} variant={"outlined"}>
                                <Grid container justifyContent={"space-between"} rowSpacing={2}>
                                    {renderDataAttributeForm(da, index)}
                                </Grid>
                            </Paper>
                        </Grid>
                    )
                })}
            </>
        )
    }

    // action button
    const renderLookUp = () => {
        return (
            <Grid size={12} sx={{ textAlign: "center" }}>
                <Collapse in={gs1KeysError()}>
                    <Alert severity={"error"} sx={{ mb: 2 }}>
                        {t("space_key_error")}
                    </Alert>
                </Collapse>
                <Collapse in={!!verifiedGtin}>
                    <Alert severity={"success"} sx={{ mb: 2 }}>
                        <Box textAlign={"left"}>
                            <b>{t("gs1_data_validated")}</b><br/>
                            {t("gs1_data_validated_sub")}
                        </Box>
                    </Alert>
                </Collapse>
                {/* Verification went stale (GS1 data / URL changed after a Look Up)
                    — the QR Code button is blocked until the user re-validates. */}
                <Collapse in={!!lookUpStale}>
                    <Alert severity={"info"} sx={{ mb: 2 }}>
                        {t("products:gs1_lookup_stale")}
                    </Alert>
                </Collapse>
                {/* Look Up rejected (e.g. short URL already in use): tell the user
                    to fix the data and run it again — the button stays enabled. */}
                <Collapse in={verifiedGtin === false}>
                    <Alert severity={"error"} sx={{ mb: 2 }}>
                        {t("products:gs1_lookup_failed_retry")}
                    </Alert>
                </Collapse>
                <Button
                    variant={verifiedGtin ? "outlined" : "contained"}
                    color={"primary"}
                    sx={{ px: 10 }}
                    onClick={handleGS1LookUp}
                    disabled={disabled || !GS1Product.primaryKeyValue || gs1KeysError()}
                >
                    {!GS1Product.primaryKeyValue ? t("products:enter_primary_key_value") : t("products:look_up")}
                </Button>
            </Grid>
        )
    }

    if (!usedModule || usedModule !== "GS1") return <></>

    return (
        <>
            {renderPrimaryKeySelector()}
            <Grid size={12}><Divider sx={{ mt: 2 }} /></Grid>
            {renderKeyQualifiersSelector()}
            <Grid size={12}><Divider sx={{ mt: 2 }} /></Grid>
            {renderDataAttributesSelector()}
            <Grid size={12}><Divider sx={{ mt: 2 }} /></Grid>
            {renderLookUp()}
        </>
    )
}